/**
 * RSVPButtons - Issue #318
 * 
 * Interactive RSVP buttons for events allowing users to indicate
 * their attendance status: Yes, Maybe, No
 */

import { useState } from 'react';
import { Check, Clock, X, Users } from 'lucide-react';
import { useCreateRSVP, useUserRSVP } from '../hooks/useRSVPs';
import { useAuth } from '../hooks/useAuth';
import { type RSVPStatus } from '../lib/api/rsvps';
import { ButtonSkeleton } from './ui/SkeletonComponents';

interface RSVPButtonsProps {
  eventId: string;
  className?: string;
}

const RSVP_OPTIONS = [
  {
    status: 'yes' as RSVPStatus,
    label: 'Going',
    icon: Check,
    variant: 'primary' as const,
    description: "I'll be there"
  },
  {
    status: 'maybe' as RSVPStatus,
    label: 'Maybe',
    icon: Clock,
    variant: 'secondary' as const,
    description: 'Not sure yet'
  },
  {
    status: 'no' as RSVPStatus,
    label: "Can't Go",
    icon: X,
    variant: 'destructive' as const,
    description: "Won't attend"
  }
];

export default function RSVPButtons({ eventId, className = '' }: RSVPButtonsProps) {
  const [selectedStatus, setSelectedStatus] = useState<RSVPStatus | null>(null);
  
  // Issue #604 Phase 2: Get user for auth-guarded query
  const { user } = useAuth();
  
  const { data: userRSVP, isLoading: loadingRSVP } = useUserRSVP(eventId, user?.id);
  const createRSVP = useCreateRSVP(eventId);

  const currentStatus = userRSVP?.rsvp?.status || null;
  const displayStatus = selectedStatus || currentStatus;

  const handleRSVP = async (status: RSVPStatus) => {
    setSelectedStatus(status);
    
    try {
      await createRSVP.mutateAsync({ status });
    } catch (error) {
      // Reset selection on error
      setSelectedStatus(null);
      console.error('Failed to update RSVP:', error);
    }
  };

  if (loadingRSVP) {
    return (
      <div className={`bg-oro-surface border border-oro-border rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-accent" />
          <span className="text-text-primary font-medium">RSVP</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <ButtonSkeleton className="h-10" />
          <ButtonSkeleton className="h-10" />
          <ButtonSkeleton className="h-10" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-oro-surface border border-oro-border rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-accent" />
        <span className="text-text-primary font-medium">RSVP</span>
        {displayStatus && (
          <span className="text-text-muted text-sm">
            · You're{' '}
            {displayStatus === 'yes' ? 'going' : displayStatus === 'maybe' ? 'maybe going' : "not going"}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {RSVP_OPTIONS.map(({ status, label, icon: Icon, description }) => {
          const isSelected = displayStatus === status;
          const isLoading = createRSVP.isPending && selectedStatus === status;
          
          return (
            <button
              key={status}
              onClick={() => handleRSVP(status)}
              disabled={createRSVP.isPending}
              className={`
                relative flex flex-col items-center justify-center p-3 rounded-lg text-center transition-all duration-200
                ${isSelected
                  ? status === 'yes'
                    ? 'bg-green-500/20 border-2 border-green-500/60 text-green-400'
                    : status === 'maybe'
                    ? 'bg-yellow-500/20 border-2 border-yellow-500/60 text-yellow-400'
                    : 'bg-red-500/20 border-2 border-red-500/60 text-red-400'
                  : status === 'no'
                  ? 'bg-oro-surface-alt border-2 border-transparent hover:border-red-500/40 text-text-secondary hover:text-red-400'
                  : 'bg-oro-surface-alt border-2 border-transparent hover:border-oro-border text-text-secondary hover:text-text-primary'
                }
                ${createRSVP.isPending ? 'opacity-50' : 'hover:scale-105'}
                ${createRSVP.isPending && selectedStatus === status ? 'animate-pulse' : ''}
              `}
              title={description}
            >
              <Icon 
                className={`w-4 h-4 mb-1 ${
                  isLoading ? 'animate-spin' : ''
                }`} 
              />
              <span className="text-xs font-medium">{label}</span>
              
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent border-2 border-oro-surface" />
              )}
            </button>
          );
        })}
      </div>

      {displayStatus && (
        <p className="text-text-muted text-xs text-center mt-3">
          {displayStatus === 'yes' && "Great! We're looking forward to seeing you."}
          {displayStatus === 'maybe' && "No worries, let us know when you decide."}
          {displayStatus === 'no' && "Thanks for letting us know. Maybe next time!"}
        </p>
      )}
    </div>
  );
}